/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { type MouseEvent } from 'react';
import { List, ListItem } from '@mui/material';
import RequiredDocumentItem from '../RequiredDocumentItem/RequiredDocumentItem';
import { DisplayedDocument } from '../../../providers/types';

export type RequiredDocumentsListProps = {
    documents: Array<DisplayedDocument>;
    onUpload: (dossierId: number, documentId: number, file: File, fichierId?: number) => void;
    onDeletion: (fichierId: number) => void;
    onDocDeletion: (DocumentId: number) => void;
    onDownload: (fichierId: number) => void;
    onMove: (label: string, fichierId: number) => void;
    onShow: (label: string, fichierId: number, documentId: number) => void;
    onSign?: (label: string, fichierId: number, fileName?: string) => void;
    onDownloadSigned?: (fichierId: number, fileName?: string) => void;
    onClick?: (documentId: number, fichierId: number) => void;
    isSubmitting?: boolean;
};

const RequiredDocumentsList: React.FunctionComponent<RequiredDocumentsListProps> = function (
    props,
) {
    const {
        documents,
        onUpload: handleUpload,
        onDeletion: handleDeletion,
        onDownload: handleDownload,
        onDocDeletion: handleDocumentDeletion,
        onMove: handleMove,
        onShow: handleShow,
        onSign: handleSign,
        onDownloadSigned: handleDownloadSigned,
        onClick: handleClick = (documentId: number, fichierId: number) => {},
        isSubmitting = false,
    } = props;

    const getItemKey = (document: DisplayedDocument) => {
        if (document.fichierId) {
            return `${document.id}-${document.fichierId}`;
        }

        return String(document.id);
    };

    const listSx = {
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px 0',
    };

    const listItemSx = {
        padding: '0',
        display: 'block',
    };

    const preventDefaultOrBubbling = (e?: MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const items = documents.map((document) => (
        <ListItem
            key={getItemKey(document)}
            sx={listItemSx}
            data-testid="required-document-list-item"
        >
            <RequiredDocumentItem
                {...document}
                onChange={
                    document.fichierId
                        ? (files: FileList) =>
                              handleUpload(
                                  document.dossierId,
                                  document.id as number,
                                  files[0],
                                  document.fichierId as number,
                              )
                        : (files: FileList) =>
                              handleUpload(
                                  document.dossierId,
                                  document.id as number,
                                  files[0],
                                  undefined,
                              )
                }
                onDeletion={
                    document.fichierId
                        ? () => {
                              handleDeletion(document.fichierId as number);
                          }
                        : () => {}
                }
                onDocDeletion={
                    document.id
                        ? () => {
                              handleDocumentDeletion(document.id as number);
                          }
                        : () => {}
                }
                onDownload={
                    document.fichierId
                        ? (e?: MouseEvent) => {
                              preventDefaultOrBubbling(e);
                              handleDownload(document.fichierId as number);
                          }
                        : () => {}
                }
                onShow={
                    document.fichierId
                        ? (e?: MouseEvent) => {
                              preventDefaultOrBubbling(e);
                              handleShow(document.label, document.fichierId as number, document.id);
                          }
                        : () => {}
                }
                onSign={
                    document.fichierId && handleSign
                        ? (e?: MouseEvent) => {
                              preventDefaultOrBubbling(e);
                              handleSign(
                                  document.label,
                                  document.fichierId as number,
                                  document.fileName,
                              );
                          }
                        : undefined
                }
                onDownloadSigned={
                    document.fichierId && handleDownloadSigned
                        ? () =>
                              handleDownloadSigned(document.fichierId as number, document.fileName)
                        : undefined
                }
                onMove={
                    document.fichierId
                        ? (e?: MouseEvent) => {
                              preventDefaultOrBubbling(e);
                              handleMove(document.label, document.fichierId as number);
                          }
                        : () => {}
                }
                onClickOnCard={(e) => {
                    preventDefaultOrBubbling(e);
                    if (document.fichierId) {
                        handleShow(document.label, document.fichierId as number, document.id);
                    }
                }}
                onClick={() => handleClick(document.id as number, document.fichierId as number)}
                isSubmitting={isSubmitting}
            />
        </ListItem>
    ));

    return <List sx={listSx}>{items}</List>;
};

export default RequiredDocumentsList;
